<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0">
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.spoofing.gssHeatmap.chartTitle"
					descriptionKey="stats.spoofing.gssHeatmap.chartTitleTooltipMedian"
					titleClass="pd-lg-x pd-md-top"
				/>
				<HeatMapChart
					v-if="mountpointsSelected.length > 0 && datesRangeStr != null"
					:isLoading="isLoading"
					:series="heatmapSeries"
					:categories="heatmapCategories"
					colorSettingsMode="exact"
					:colorSettings="colorSettings"
					:noDataLabel="$t('stats.spoofing.gssHeatmap.legend.noData')"
					showLabelNamesInTooltip
					:xAxisLabel="$t('stats.spoofing.gssHeatmap.xAxisLabel')"
					:tooltipFooter="$t('stats.spoofing.gssHeatmap.tooltipDualFrequencyNote')"
				/>
				<EmptyChartFiltersMessage v-else messageKey="stats.spoofing.gssHeatmap.mountpointsPlaceholder" />
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<BaseStationSelect
							v-model="mountpointsSelected"
							:onUpdate="onMountpointsUpdate"
							:placeholderKey="'stats.spoofing.gssHeatmap.mountpointsPlaceholder'"
						/>
						<SubNetworkSelect
							v-if="showSubNetworkSelect"
							ref="subNetworkSelect"
							:onChange="setMountpointsForSubnetwork"
							:allowClear="true"
						/>
						<div class="pd-vertical">
							<span>{{ $t('stats.spoofing.gssHeatmap.visibilityThresholdLabel') }}</span>
							<Select
								class="width-100"
								v-model:value="visibilityThreshold"
								:options="visibilityThresholdOptions"
							/>
						</div>
						<DateRangeChooser
							v-if="showDateRangeChooser"
							v-model="datesRange"
							quickPeriod="day1"
							@change="fetchStatsItems"
						/>
					</VerticalLayout>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Row, Col, Card, Select } from 'ant-design-vue';

import Api from '@/api/Api';
import { useBaseStationsStore } from '@/stores/baseStations';

import { useLocalDateRange } from '@/utils/useDateRangeAdapter';
import { useSubNetworkMountpointsFilter } from '@/utils/useSubNetworkMountpointsFilter';
import { generateSeries, pointEntryFactoryXYDay } from '@/utils/chartsUtils';
import type { ChartSeries } from '@/utils/types/chartsUtilsTypes';
import {
	buildGssMedianAggregatedChartItems,
	buildHeatmapColorSettings,
} from '@/utils/spoofingGssMedianAggregatedHeatmapUtils';
import type { GssMedianAggregatedItem } from '@/utils/spoofingGssMedianAggregatedHeatmapUtils';
import type { HeatmapTooltipRow } from '@/utils/spoofingVisibilityHeatmapUtils';

import { addDays, datetimeToString, jsNativeDateToDayJs } from '@/components/common/dateUtils';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import DateRangeChooser from '@/components/common/DateRangeChooser.vue';
import BaseStationSelect from '@/components/common/controls/BaseStationSelect.vue';
import SubNetworkSelect from '@/components/common/controls/SubNetworkSelect.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage.vue';
import HeatMapChart from '@/components/charts/HeatMapChart.vue';


const VISIBILITY_THRESHOLD_OPTIONS = Array.from({ length: 11 }, (_, index) => {
	const value = 50 + index * 5;
	return {
		value,
		label: String(value) + '%',
	};
});

const DEFAULT_VISIBILITY_THRESHOLD = 75;

type HeatmapChartSeries = {
	name: string;
	hidden: boolean;
	data: Array<{ x: unknown; y: number | null; data: HeatmapTooltipRow[] }>;
};


const mapHeatmapSeries = (series: ChartSeries[]): HeatmapChartSeries[] => {
	return series.map(serie => ({
		name: serie.name,
		hidden: serie.hidden,
		data: serie.data.map(point => {
			if (Array.isArray(point)) {
				return { x: point[0], y: point[1], data: [] as HeatmapTooltipRow[] };
			}
			const tooltipRows = Array.isArray(point.tooltipRows)
				? point.tooltipRows as HeatmapTooltipRow[]
				: [];
			return {
				x: point.x,
				y: point.y,
				data: tooltipRows,
			};
		}),
	}));
};


export default {
	components: {
		Row, Col, Card, Select,
		VerticalLayout, DateRangeChooser, BaseStationSelect, SubNetworkSelect,
		StatsChartTitleWithDescription, EmptyChartFiltersMessage, HeatMapChart,
	},
	props: {
		initMountpointSelected: { type: String, default: null },
		minDate: { type: Date, default: null },
		maxDate: { type: Date, default: null },
		showDateRangeChooser: { type: Boolean, default: true },
		showSubNetworkSelect: { type: Boolean, default: true },
	},
	setup(props: {
		initMountpointSelected: string | null;
		minDate: Date | null;
		maxDate: Date | null;
		showDateRangeChooser: boolean;
		showSubNetworkSelect: boolean;
	}) {
		const { t } = useI18n();
		const { datesRange, datesRangeStr, datesRangeSafe } = useLocalDateRange(1, 0);
		if (props.minDate != null && props.maxDate != null) {
			datesRange.value = [
				jsNativeDateToDayJs(props.minDate),
				jsNativeDateToDayJs(addDays(props.maxDate, 1)),
			];
		}
		const mountpointsSelected = ref<string[]>([]);
		const baseStationsStore = useBaseStationsStore();
		const visibilityThreshold = ref(DEFAULT_VISIBILITY_THRESHOLD);
		const { apiData, isLoading, doRequest } = Api.useSpoofingGssAggregatedGet();

		const fetchStatsItems = () => {
			if (datesRangeStr.value == null || mountpointsSelected.value.length === 0) {
				return;
			}
			doRequest({
				start_date: datesRangeStr.value[0],
				end_date: datesRangeStr.value[1],
				mountpoints: mountpointsSelected.value,
			});
		};

		const {
			subNetworkSelect,
			setMountpointsForSubnetwork,
			setSubnetworkOnMountpointsChange,
		} = useSubNetworkMountpointsFilter(mountpointsSelected, fetchStatsItems);
		const onMountpointsUpdate = () => {
			if (props.showSubNetworkSelect) {
				setSubnetworkOnMountpointsChange();
			} else {
				fetchStatsItems();
			}
		};
		watch(
			[() => props.initMountpointSelected, () => baseStationsStore.mountpointsSelectOptions],
			([mountpoint, options]) => {
				if (mountpoint == null || !options.some(option => option.value === mountpoint)) {
					return;
				}
				if (mountpointsSelected.value.length === 1 && mountpointsSelected.value[0] === mountpoint) {
					return;
				}
				mountpointsSelected.value = [mountpoint];
				fetchStatsItems();
			},
			{ immediate: true },
		);

		const heatmapSeriesCategories = computed(() => {
			if (datesRangeSafe.value[0] == null || datesRangeSafe.value[1] == null) {
				return {
					series: [] as HeatmapChartSeries[],
					categories: [] as string[],
				};
			}
			const chartItems = buildGssMedianAggregatedChartItems(
				(apiData.value.items || []) as GssMedianAggregatedItem[],
				visibilityThreshold.value,
				t('stats.spoofing.gssHeatmap.messagesCountLabel'),
			);
			const result = generateSeries({
				items: chartItems as unknown as Array<Record<string, unknown>>,
				getSeriesKeyFn: item => String(item.mountpoint),
				getValueFn: item => (typeof item.status === 'number' ? item.status : null),
				seriesStep: apiData.value.period || 5,
				seriesStepUnit: 'minute',
				categoryFormatter: datetimeToString,
				pointEntryFactory: pointEntryFactoryXYDay,
				showNullAsZero: false,
				minDate: new Date(datesRangeSafe.value[0]),
				maxDate: new Date(datesRangeSafe.value[1]),
				forceSeriesKeys: mountpointsSelected.value,
				roundTimeValues: true,
				pushAdditionalFields: ['tooltipRows'],
			});
			return {
				series: mapHeatmapSeries(result.series),
				categories: result.categories.map(category => String(category)),
			};
		});

		const heatmapSeries = computed(() => heatmapSeriesCategories.value.series);
		const heatmapCategories = computed(() => heatmapSeriesCategories.value.categories);

		const colorSettings = computed(() => buildHeatmapColorSettings(
			t('stats.spoofing.gssHeatmap.legend.ok'),
			t('stats.spoofing.gssHeatmap.legend.multi'),
			{},
			t('stats.spoofing.gssHeatmap.legend.single'),
			false,
			t('stats.spoofing.gssHeatmap.legend.break'),
		));

		return {
			isLoading,
			datesRange,
			datesRangeStr,
			mountpointsSelected,
			visibilityThreshold,
			visibilityThresholdOptions: VISIBILITY_THRESHOLD_OPTIONS,
			fetchStatsItems,
			onMountpointsUpdate,
			subNetworkSelect,
			setMountpointsForSubnetwork,
			heatmapSeries,
			heatmapCategories,
			colorSettings,
		};
	},
};
</script>
